/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Button } from '@mui/material';
import { forwardRef } from 'react';
/**
 * ActionButton
 *
 * A reusable wrapper around MUI's `<Button>` providing consistent styling,
 * labeling, and behavior across the application.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.label - Button label (visible text)
 * @param {Function} props.onClick - Event handler triggered on click
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {'contained' | 'outlined' | 'text'} [props.variant='contained'] - MUI button variant
 * @param {'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'} [props.color='primary'] - MUI color variant
 * @param {'button' | 'submit' | 'reset'} [props.buttonType='button'] - HTML button type
 * @param {'small' | 'medium' | 'large'} [props.size='small'] - Button size
 * @param {Object} [props.sx={}] - Additional MUI system styles
 * @param {string} [props.id] - Optional DOM id attribute
 *
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref for direct DOM access
 *
 * @returns {JSX.Element} Rendered Button component
 */
const ActionButton = forwardRef(
  (
    {
      label,
      onClick,
      disabled = false,
      variant = 'contained',
      color = 'primary',
      type = 'clear',
      size = 'small',
      sx = {},
      id = '',
    },
    ref
  ) => (
    <Button
      type={type}
      variant={variant}
      color={color}
      onClick={onClick}
      disabled={disabled}
      size={size}
      sx={sx}
      id={id}
      ref={ref}
    >
      {label?.toUpperCase()}
    </Button>
  )
);

export default ActionButton;
